import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { orderWidgets } from "@/lib/dashboardWidgets";
import { Loader2, School, LayoutDashboard, Wallet, CalendarCheck, Settings2, Image as ImageIcon, IdCard, Users2, Banknote, ChevronUp, ChevronDown, AlignLeft, AlignCenter, AlignRight, MessageSquare, Fingerprint, ShieldCheck } from "lucide-react";
import { setDateFormat } from "@/lib/helpers";
import { useSchool } from "@/context/SchoolContext";
import { useAuth } from "@/context/AuthContext";
import LoginSettingsTab from "@/pages/LoginSettingsTab";
import StudentSettingsTab from "@/pages/StudentSettingsTab";
import StaffPermissionsTab from "@/pages/StaffPermissionsTab";
import { toast } from "sonner";

function NewSessionBox() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && !sessions.length) api.get("/sessions").then((r) => setSessions(r.data || [])).catch(() => {}); }, [open, sessions.length]);
  const create = async () => {
    if (!name.trim()) { toast.error("Enter a session name"); return; }
    setSaving(true);
    try {
      const r = await api.post("/sessions/setup", { name: name.trim(), clone_from: cloneFrom });
      toast.success(`Session "${r.data.session}" ready · ${r.data.classes_cloned} classes cloned`);
      setOpen(false); setName(""); setCloneFrom("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create session"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="new-session-box">
      {!open ? (
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="new-session-open">+ Create New Session</Button>
      ) : (
        <div className="space-y-3 max-w-md">
          <div><Label>New session name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2027-28" className="mt-1" data-testid="new-session-name" /></div>
          <div>
            <Label>Clone classes &amp; sections from</Label>
            <Select value={cloneFrom || "__none__"} onValueChange={(v) => setCloneFrom(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="new-session-clone"><SelectValue placeholder="Don't clone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Don't clone —</SelectItem>
                {sessions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">Creates the same classes &amp; sections in the new session (no students or fees).</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={create} disabled={saving} data-testid="new-session-create">{saving ? "Creating…" : "Create Session"}</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_FORMULA = [{ sign: "+", status: "Present" }, { sign: "+", status: "Late" }, { sign: "+", status: "Half Day" }, { sign: "+", status: "Holiday" }, { sign: "+", status: "Paid Leave" }];

const MARKSHEET_ITEMS = [
  { k: "school_name", label: "School Name" },
  { k: "logo", label: "School Logo" },
  { k: "tag_line", label: "Tag Line" },
  { k: "school_code", label: "School Code" },
  { k: "affiliated_to", label: "Affiliated To" },
  { k: "udise_code", label: "UDISE Code" },
  { k: "address", label: "School Address" },
  { k: "school_number", label: "School Number" },
  { k: "watermark_logo", label: "Watermark Logo" },
  { k: "marksheet_qr", label: "Marksheet QR Code" },
];

const IDCARD_ITEMS = [
  { k: "logo", label: "School Logo", unit: "mm" },
  { k: "school_name", label: "School Name", unit: "pt" },
  { k: "school_address", label: "School Address", unit: "pt" },
  { k: "school_phone", label: "School Phone", unit: "pt" },
  { k: "photo", label: "Student Photo", unit: "mm" },
  { k: "name", label: "Student Name", unit: "pt" },
  { k: "father_name", label: "Father's Name", unit: "pt" },
  { k: "class", label: "Class", unit: "pt" },
  { k: "blood_group", label: "Blood Group", unit: "pt" },
  { k: "address", label: "Address", unit: "pt" },
  { k: "phone", label: "Phone", unit: "pt" },
  { k: "principal_signature", label: "Principal Signature", unit: "mm" },
];

const IDCARD_SIZE_DEFAULTS = { logo: 10, school_name: 8, school_address: 4.5, school_phone: 4.5, photo: 22, name: 7.5, father_name: 5.5, class: 5.5, blood_group: 5.5, address: 5.5, phone: 5.5, principal_signature: 10 };

export default function Settings() {
  const [f, setF] = useState(null);
  const [dbSessions, setDbSessions] = useState([]);
  const [notifyStatus, setNotifyStatus] = useState({ sms: false, whatsapp: false });
  const [udiseOpts, setUdiseOpts] = useState({ student: [], staff: [], studentDefault: [], staffDefault: [] });
  const [tab, setTab] = useState("school");
  const { refreshSchool } = useSchool();
  const { user } = useAuth();

  useEffect(() => { api.get("/settings").then((r) => setF(r.data && r.data.school_name ? r.data : { school_name: "SSSPS", allow_partial: true })); }, []);
  useEffect(() => { api.get("/notify/status").then((r) => setNotifyStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/sessions").then((r) => setDbSessions((r.data || []).map((s) => s.name))).catch(() => {}); }, []);
  useEffect(() => {
    Promise.all([api.get("/students/export-fields"), api.get("/staff/export-fields")])
      .then(([s, t]) => setUdiseOpts({ student: s.data.fields || [], staff: t.data.fields || [], studentDefault: s.data.udise_default || [], staffDefault: t.data.udise_default || [] }))
      .catch(() => {});
  }, []);
  const set = (k, v) => setF({ ...f, [k]: v });
  const renderUdiseCol = (title, key, opts, def) => {
    const cur = f[key] === undefined ? def : f[key];
    const allOn = opts.length > 0 && cur.length === opts.length;
    const toggle = (fk) => set(key, cur.includes(fk) ? cur.filter((x) => x !== fk) : [...cur, fk]);
    return (
      <div data-testid={`udise-col-${key}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-800">{title}</h4>
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" data-testid={`udise-selectall-${key}`}>
            <Checkbox checked={allOn} onCheckedChange={() => set(key, allOn ? [] : opts.map((o) => o.key))} />Select All
          </label>
        </div>
        <div className="space-y-0.5 max-h-72 overflow-auto border border-slate-100 rounded-lg p-2">
          {opts.map((o) => (
            <label key={o.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`udise-${key}-${o.key}`}>
              <Checkbox checked={cur.includes(o.key)} onCheckedChange={() => toggle(o.key)} />
              <span className="text-slate-600">{o.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };
  const fRef = useRef(f); fRef.current = f;
  const setRef = useRef(set); setRef.current = set;
  const F = useCallback(({ k, label, type = "text" }) => (
    <div><Label>{label}</Label><Input type={type} value={(fRef.current || {})[k] || ""} onChange={(e) => setRef.current(k, e.target.value)} data-testid={`setting-${k}`} /></div>
  ), []);

  const ImageUpload = useCallback(({ k, label, hint }) => {
    const value = (fRef.current || {})[k] || "";
    const onFile = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
      const reader = new FileReader();
      reader.onload = () => setRef.current(k, reader.result);
      reader.readAsDataURL(file);
    };
    return (
      <div>
        <Label>{label}</Label>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
        <div className="mt-2 flex items-center gap-3">
          <div className="h-20 w-20 shrink-0 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
            {value ? <img src={value} alt={label} className="h-full w-full object-contain" /> : <ImageIcon size={22} className="text-slate-300" />}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="cursor-pointer inline-flex items-center justify-center h-8 px-3 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-testid={`upload-btn-${k}`}>
              {value ? "Change" : "Upload"}
              <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`upload-${k}`} />
            </label>
            {value && <Button type="button" size="sm" variant="ghost" className="text-rose-600 h-7" onClick={() => setRef.current(k, "")} data-testid={`remove-${k}`}>Remove</Button>}
          </div>
        </div>
      </div>
    );
  }, []);

  const save = async () => {
    try { await api.put("/settings", f); setDateFormat(f.date_format); refreshSchool(); toast.success("Settings saved"); } catch (e) { toast.error("Failed"); }
  };
  const saveNotify = async () => {
    try {
      await api.put("/settings", f);
      const r = await api.get("/notify/status"); setNotifyStatus(r.data);
      toast.success("Notification settings saved");
    } catch (e) { toast.error("Failed"); }
  };

  if (!f) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const panel = "bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-2 gap-4";
  const card = "bg-white rounded-xl border border-slate-200 p-6";
  const updFormula = (i, key, val) => {
    const arr = (f.salary_formula && f.salary_formula.length ? f.salary_formula : DEFAULT_FORMULA).map((x) => ({ ...x }));
    arr[i] = { ...arr[i], [key]: val };
    set("salary_formula", arr);
  };
  const sessionOptions = (() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let i = y - 3; i <= y + 3; i++) arr.push(`${i}-${String(i + 1).slice(2)}`);
    dbSessions.forEach((s) => { if (s && !arr.includes(s)) arr.push(s); });
    if (f.academic_session && !arr.includes(f.academic_session)) arr.unshift(f.academic_session);
    return arr;
  })();
  const sessionMonths = (() => {
    const m = String(f.academic_session || "").match(/(\d{4})/);
    if (!m) return [];
    const start = parseInt(m[1], 10);
    const names = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
    return names.map((mn, i) => `${mn} ${i < 9 ? start : start + 1}`);
  })();

  return (
    <div className="space-y-6 w-full max-w-[90%] mx-auto">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">School Settings</h1>
        {tab !== "student" && tab !== "staff" && <Button onClick={save} data-testid="save-settings-btn">Save Settings</Button>}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="settings-tabs">
          <TabsTrigger value="school" className="gap-1.5" data-testid="settings-tab-school"><School size={15} />School</TabsTrigger>
          <TabsTrigger value="dashboard" className="gap-1.5" data-testid="settings-tab-dashboard"><LayoutDashboard size={15} />Dashboard</TabsTrigger>
          <TabsTrigger value="student" className="gap-1.5" data-testid="settings-tab-student"><Users2 size={15} />Student</TabsTrigger>
          <TabsTrigger value="staff" className="gap-1.5" data-testid="settings-tab-staff"><ShieldCheck size={15} />Staff</TabsTrigger>
          <TabsTrigger value="fees" className="gap-1.5" data-testid="settings-tab-fees"><Wallet size={15} />Fees</TabsTrigger>
          <TabsTrigger value="attendance" className="gap-1.5" data-testid="settings-tab-attendance"><CalendarCheck size={15} />Attendance</TabsTrigger>
          <TabsTrigger value="salary" className="gap-1.5" data-testid="settings-tab-salary"><Banknote size={15} />Salary</TabsTrigger>
          <TabsTrigger value="notify" className="gap-1.5" data-testid="settings-tab-notify"><MessageSquare size={15} />Notifications</TabsTrigger>
          <TabsTrigger value="login" className="gap-1.5" data-testid="settings-tab-login"><Fingerprint size={15} />Login</TabsTrigger>
          <TabsTrigger value="more" className="gap-1.5" data-testid="settings-tab-more"><Settings2 size={15} />More</TabsTrigger>
        </TabsList>

        <TabsContent value="school" className="space-y-6">
          <section className={card} data-testid="settings-section-basic">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Basic Details</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <F k="school_name" label="School Name" />
              <F k="tag_line" label="Tag Line" />
              <F k="school_code" label="School Code" />
              <F k="affiliated_to" label="Affiliated To" />
              <F k="affiliation_number" label="Affiliation Number / Code" />
              <F k="udise_code" label="UDISE Code" />
            </div>
          </section>

          <section className={card} data-testid="settings-section-contact">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Contact Details</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <F k="contact_person_name" label="Contact Person Name (School)" />
              <F k="contact_person_mobile" label="Contact Person Mobile Number" />
              <F k="school_number" label="School Number" />
              <F k="principal_name" label="Principal Name" />
              <F k="principal_number" label="Principal Number" />
              <F k="accountant_number" label="Accountant Number" />
              <F k="email" label="Email ID" />
              <F k="website" label="Website" />
              <div className="sm:col-span-2 lg:col-span-3"><F k="address" label="School Address" /></div>
              <F k="city" label="City" />
              <F k="state" label="State" />
              <F k="pin_code" label="Pin Code" />
            </div>
          </section>

          <section className={card} data-testid="settings-section-logo">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Logo, Signature & More</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <ImageUpload k="logo" label="School Logo" hint="Upload your school's logo" />
              <ImageUpload k="watermark_logo" label="Watermark Logo" hint="Upload logo for watermark" />
              <ImageUpload k="authority_signature" label="Authority Signature (Principal)" hint="Upload signature image" />
              <ImageUpload k="marksheet_qr" label="Marksheet QR Code" hint="Upload QR code for marksheets" />
            </div>
          </section>

          <section className={card} data-testid="settings-section-session">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Session</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Select Current Session</Label>
                <Select value={f.academic_session || ""} onValueChange={(v) => set("academic_session", v)}>
                  <SelectTrigger data-testid="setting-academic_session"><SelectValue placeholder="Select session" /></SelectTrigger>
                  <SelectContent>
                    {sessionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label>School Months (Session Months)</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="session-months">
                {sessionMonths.length ? sessionMonths.map((mn) => (
                  <span key={mn} className="px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium">{mn}</span>
                )) : <span className="text-xs text-slate-400">Select a session to see months</span>}
              </div>
            </div>
            <NewSessionBox />
          </section>
        </TabsContent>

        <TabsContent value="dashboard" className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
          <p className="text-sm text-slate-500">Toggle what appears on the Dashboard and use the arrows to reorder widgets. Hidden items won't show for anyone.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {orderWidgets(f.dashboard_order).map((w, idx, arr) => {
              const hidden = f.dashboard_hidden || [];
              const shown = !hidden.includes(w.key);
              const moveDash = (dir) => {
                const keys = arr.map((x) => x.key);
                const i = keys.indexOf(w.key), j = i + dir;
                if (i < 0 || j < 0 || j >= keys.length) return;
                [keys[i], keys[j]] = [keys[j], keys[i]];
                set("dashboard_order", keys);
              };
              return (
                <div key={w.key} className="flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg border border-slate-200" data-testid={`dash-widget-${w.key}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex flex-col -space-y-1 shrink-0">
                      <button type="button" aria-label="Move up" title="Move up" disabled={idx === 0} onClick={() => moveDash(-1)} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`dash-up-${w.key}`}><ChevronUp size={15} /></button>
                      <button type="button" aria-label="Move down" title="Move down" disabled={idx === arr.length - 1} onClick={() => moveDash(1)} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`dash-down-${w.key}`}><ChevronDown size={15} /></button>
                    </div>
                    <div className="font-medium text-slate-800 text-sm truncate">{w.label}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-medium ${shown ? "text-emerald-600" : "text-slate-400"}`}>{shown ? "Shown" : "Hidden"}</span>
                    <Switch checked={shown} onCheckedChange={(v) => set("dashboard_hidden", v ? hidden.filter((k) => k !== w.key) : [...hidden, w.key])} data-testid={`dash-toggle-${w.key}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="student">
          <StudentSettingsTab />
        </TabsContent>

        <TabsContent value="staff">
          <StaffPermissionsTab />
        </TabsContent>

        <TabsContent value="fees" className={panel}>
          <F k="receipt_prefix" label="Receipt Prefix" />
          <F k="fee_due_date" label="Fee Due Date" />
          <F k="late_fee" label="Late Fee" type="number" />
          <F k="payment_link" label="Online Payment Link (for reminders)" />
          <div className="sm:col-span-2"><F k="receipt_footer" label="Receipt Footer" /></div>
          <div className="sm:col-span-2">
            <Label>Reminder Message Template</Label>
            <textarea value={f.reminder_template || ""} onChange={(e) => set("reminder_template", e.target.value)} data-testid="setting-reminder_template" rows={3}
              className="w-full mt-1 rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Dear Parent, a fee of {due} is pending for {name} ({admission_number}) at {school}. Please pay by {due_date}. Pay online: {payment_link}" />
            <p className="text-xs text-slate-400 mt-1">Placeholders: {"{name}"}, {"{admission_number}"}, {"{father_name}"}, {"{due}"}, {"{due_date}"}, {"{school}"}, {"{payment_link}"}. Leave blank for the default message.</p>
          </div>
          <div className="sm:col-span-2 flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div><div className="font-medium text-slate-800">Allow Partial Payments</div><div className="text-xs text-slate-400">Accept payments less than the full due amount</div></div>
            <Switch checked={!!f.allow_partial} onCheckedChange={(v) => set("allow_partial", v)} data-testid="setting-partial" />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div><div className="font-medium text-slate-800">Allow Discounts</div><div className="text-xs text-slate-400">Show the discount field at collection & allow concessions</div></div>
            <Switch checked={f.allow_discount !== false} onCheckedChange={(v) => set("allow_discount", v)} data-testid="setting-discount" />
          </div>
          <div className="sm:col-span-2 p-3 rounded-lg bg-slate-50 space-y-3">
            <div className="flex items-center justify-between">
              <div><div className="font-medium text-slate-800">Late Fine</div><div className="text-xs text-slate-400">Auto-add a fine for each overdue, unpaid month</div></div>
              <Switch checked={!!f.late_fine_enabled} onCheckedChange={(v) => set("late_fine_enabled", v)} data-testid="setting-late-fine" />
            </div>
            {f.late_fine_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fine per overdue month (₹)</Label><Input type="number" value={f.late_fine_per_month || ""} onChange={(e) => set("late_fine_per_month", e.target.value)} data-testid="setting-late-fine-amount" /></div>
                <div><Label>Applies from day of month</Label><Input type="number" value={f.late_fine_day || 11} onChange={(e) => set("late_fine_day", e.target.value)} data-testid="setting-late-fine-day" /></div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="space-y-6">
          <section className={card} data-testid="settings-attendance-student">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Users2 size={16} className="text-indigo-600" />Student Attendance</h3>
            <div className="space-y-3">
              <F k="student_working_days" label="Working Days / Month (students)" type="number" />
              <p className="text-xs text-slate-400">Reference figure used when computing student monthly attendance % in reports. Student & staff holidays are configured separately from the Attendance page's Holiday Calendar.</p>
            </div>
          </section>
          <section className={card} data-testid="settings-attendance-staff">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><IdCard size={16} className="text-indigo-600" />Staff Attendance</h3>
            <div className="space-y-3">
              <div className="pt-1">
                <Label>Attendance Status Options</Label>
                <p className="text-xs text-slate-400 mb-2">Enable which statuses can be marked for staff.</p>
                <div className="space-y-2">
                  {["Present", "Absent", "Leave", "Late", "Half Day", "Holiday"].map((st) => {
                    const list = (f.staff_attendance_statuses && f.staff_attendance_statuses.length) ? f.staff_attendance_statuses : ["Present", "Absent", "Leave", "Late", "Half Day", "Holiday"];
                    const on = list.includes(st);
                    return (
                      <div key={st} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-200" data-testid={`staff-status-${st.replace(" ", "-")}`}>
                        <span className="text-sm font-medium text-slate-800">{st}</span>
                        <Switch checked={on} onCheckedChange={(v) => set("staff_attendance_statuses", v ? [...new Set([...list, st])] : list.filter((x) => x !== st))} data-testid={`staff-status-toggle-${st.replace(" ", "-")}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="salary" className="space-y-6">
          <section className={card} data-testid="settings-section-salary">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Banknote size={16} className="text-indigo-600" />Salary Settings</h3>
            <div className="space-y-3 max-w-xl">
              <F k="working_days" label="Working Days / Month (for salary)" type="number" />
              <p className="text-xs text-slate-400">Used to calculate per-day salary and auto leave/late/half-day deductions in Payroll.</p>
              <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-200" data-testid="setting-count-late">
                <div><div className="font-medium text-slate-800 text-sm">Count Late as Leave</div><div className="text-xs text-slate-400">Convert repeated lates into leave for salary deduction</div></div>
                <Switch checked={!!f.count_late_as_leave} onCheckedChange={(v) => set("count_late_as_leave", v)} data-testid="setting-count-late-toggle" />
              </div>
              {f.count_late_as_leave && (
                <div className="flex items-center gap-2 text-sm px-3">
                  <Input type="number" value={f.late_per ?? 4} onChange={(e) => set("late_per", e.target.value)} className="w-16 h-9" data-testid="setting-late-per" />
                  <span className="text-slate-500">late =</span>
                  <Input type="number" value={f.leave_per ?? 1} onChange={(e) => set("leave_per", e.target.value)} className="w-16 h-9" data-testid="setting-leave-per" />
                  <span className="text-slate-500">leave</span>
                </div>
              )}
              <div className="pt-3 border-t border-slate-100">
                <Label>Calculate Salary (formula)</Label>
                <p className="text-xs text-slate-400 mb-2">Statuses added (+) count as paid days; subtracted (−) reduce salary. Salary deduction = basic × (subtracted days ÷ working days).</p>
                <div className="flex flex-wrap items-center gap-1.5" data-testid="salary-formula">
                  {(f.salary_formula && f.salary_formula.length ? f.salary_formula : DEFAULT_FORMULA).map((term, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <Select value={term.sign} onValueChange={(v) => updFormula(i, "sign", v)}><SelectTrigger className="w-14 h-9" data-testid={`formula-sign-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="+">+</SelectItem><SelectItem value="-">−</SelectItem></SelectContent></Select>
                      <Select value={term.status} onValueChange={(v) => updFormula(i, "status", v)}><SelectTrigger className="w-28 h-9" data-testid={`formula-status-${i}`}><SelectValue /></SelectTrigger><SelectContent>{["Present", "Late", "Half Day", "Holiday", "Paid Leave", "Leave", "Absent"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2" data-testid="salary-formula-preview">Formula: {(f.salary_formula && f.salary_formula.length ? f.salary_formula : DEFAULT_FORMULA).map((t) => `${t.sign === "-" ? "−" : "+"} ${t.status}`).join(" ")}</p>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="notify" className="space-y-6">
          <section className={card} data-testid="settings-section-notify">
            <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><MessageSquare size={16} className="text-indigo-600" />SMS & WhatsApp (Twilio)</h3>
            <p className="text-xs text-slate-400 mb-4">Enter your Twilio credentials to send fee reminders & parent logins over SMS and WhatsApp. Leave blank to use the system-wide gateway (if configured). Get these from <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-indigo-600 underline">console.twilio.com</a>.</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              <div><Label>Account SID</Label><Input value={f.twilio_account_sid || ""} onChange={(e) => set("twilio_account_sid", e.target.value)} placeholder="ACxxxxxxxxxxxxxxxx" data-testid="setting-twilio_account_sid" /></div>
              <div><Label>Auth Token</Label><Input type="password" value={f.twilio_auth_token || ""} onChange={(e) => set("twilio_auth_token", e.target.value)} placeholder="••••••••••••" data-testid="setting-twilio_auth_token" /></div>
              <div><Label>SMS Sender Number</Label><Input value={f.twilio_sms_from || ""} onChange={(e) => set("twilio_sms_from", e.target.value)} placeholder="+1XXXXXXXXXX or Messaging Service SID" data-testid="setting-twilio_sms_from" /><p className="text-xs text-slate-400 mt-1">Your Twilio phone number or Messaging Service SID (MGxxxx) for SMS.</p></div>
              <div><Label>WhatsApp Sender Number</Label><Input value={f.twilio_whatsapp_from || ""} onChange={(e) => set("twilio_whatsapp_from", e.target.value)} placeholder="+14155238886" data-testid="setting-twilio_whatsapp_from" /><p className="text-xs text-slate-400 mt-1">Your approved Twilio WhatsApp sender number.</p></div>
            </div>
            <div className="flex items-center gap-4 mt-4 text-sm">
              <span className={`flex items-center gap-1.5 ${notifyStatus.sms ? "text-emerald-600" : "text-slate-400"}`} data-testid="notify-status-sms"><span className={`h-2 w-2 rounded-full ${notifyStatus.sms ? "bg-emerald-500" : "bg-slate-300"}`} />SMS {notifyStatus.sms ? "ready" : "not configured"}</span>
              <span className={`flex items-center gap-1.5 ${notifyStatus.whatsapp ? "text-emerald-600" : "text-slate-400"}`} data-testid="notify-status-whatsapp"><span className={`h-2 w-2 rounded-full ${notifyStatus.whatsapp ? "bg-emerald-500" : "bg-slate-300"}`} />WhatsApp {notifyStatus.whatsapp ? "ready" : "not configured"}</span>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="login">
          {f && <LoginSettingsTab user={user} f={f} set={set} save={save} />}
        </TabsContent>

        <TabsContent value="more" className="space-y-6">
          <section className={card}>
            <h3 className="font-heading font-bold text-slate-900 mb-4">General</h3>
            <div>
              <Label>Date Format</Label>
              <Select value={f.date_format || "DD/MM/YYYY"} onValueChange={(v) => set("date_format", v)}>
                <SelectTrigger data-testid="setting-date_format"><SelectValue placeholder="Select date format" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (31/12/2026)</SelectItem>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2026)</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2026-12-31)</SelectItem>
                  <SelectItem value="DD-MM-YYYY">DD-MM-YYYY (31-12-2026)</SelectItem>
                  <SelectItem value="DD.MM.YYYY">DD.MM.YYYY (31.12.2026)</SelectItem>
                  <SelectItem value="DD MMM YYYY">DD MMM YYYY (31 Dec 2026)</SelectItem>
                  <SelectItem value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2026)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Preferred date format used across the app.</p>
            </div>
          </section>

          <section className={card} data-testid="settings-section-udise">
            <h3 className="font-heading font-bold text-slate-900 mb-1">UDISE+ Export Fields</h3>
            <p className="text-sm text-slate-500 mb-3">Pick the fields included when you click "Download in UDISE+ Format" in the Export dialog. Students and staff are configured separately. Leave a column with no fields ticked to use the standard UDISE default set. Columns always export in the standard field order.</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {renderUdiseCol("Student UDISE Fields", "udise_student_fields", udiseOpts.student, udiseOpts.studentDefault)}
              {renderUdiseCol("Staff UDISE Fields", "udise_staff_fields", udiseOpts.staff, udiseOpts.staffDefault)}
            </div>
          </section>

          <section className={card} data-testid="settings-section-marksheet">
            <h3 className="font-heading font-bold text-slate-900 mb-1">Marksheet</h3>
            <p className="text-sm text-slate-500 mb-2">Choose which details appear on the marksheet.</p>
            {MARKSHEET_ITEMS.map((it) => {
              const base = (f.marksheet_fields && f.marksheet_fields.length) ? f.marksheet_fields : MARKSHEET_ITEMS.map((i) => i.k);
              const enabled = base.includes(it.k);
              return (
                <label key={it.k} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 cursor-pointer" data-testid={`marksheet-item-${it.k}`}>
                  <Checkbox checked={enabled} onCheckedChange={(v) => set("marksheet_fields", v ? [...new Set([...base, it.k])] : base.filter((x) => x !== it.k))} data-testid={`marksheet-toggle-${it.k}`} />
                  <span className="text-sm font-medium text-slate-800">{it.label}</span>
                </label>
              );
            })}
          </section>

          <section className={card} data-testid="settings-section-idcard">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <h3 className="font-heading font-bold text-slate-900">ID Cards</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Line spacing</span>
                <Input type="number" step="0.1" min="0.5" max="3" className="w-20 h-8" value={f.idcard_line_spacing ?? 1} onChange={(e) => set("idcard_line_spacing", parseFloat(e.target.value) || 1)} data-testid="idcard-line-spacing" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-2">Toggle fields, set size &amp; alignment, and reorder with the arrows. Alignment applies to the student detail rows.</p>
            {(() => {
              const savedOrder = (f.idcard_order && f.idcard_order.length) ? f.idcard_order : IDCARD_ITEMS.map((i) => i.k);
              const ordered = [...savedOrder.map((k) => IDCARD_ITEMS.find((i) => i.k === k)).filter(Boolean), ...IDCARD_ITEMS.filter((i) => !savedOrder.includes(i.k))];
              const base = (f.idcard_fields && f.idcard_fields.length) ? f.idcard_fields : IDCARD_ITEMS.map((i) => i.k);
              const sizes = f.idcard_sizes || {};
              const aligns = f.idcard_align || {};
              const move = (k, dir) => {
                const cur = [...ordered.map((i) => i.k)];
                const i = cur.indexOf(k), j = i + dir;
                if (i < 0 || j < 0 || j >= cur.length) return;
                [cur[i], cur[j]] = [cur[j], cur[i]];
                set("idcard_order", cur);
              };
              const alignBtn = (k, val, Icon, cur) => (
                <button type="button" onClick={() => set("idcard_align", { ...aligns, [k]: val })} className={`p-1 rounded ${cur === val ? "bg-indigo-100 text-indigo-600" : "text-slate-400 hover:bg-slate-100"}`} data-testid={`idcard-align-${k}-${val}`}><Icon size={14} /></button>
              );
              return ordered.map((it, idx) => {
                const enabled = base.includes(it.k);
                const sizeVal = sizes[it.k] ?? IDCARD_SIZE_DEFAULTS[it.k];
                const isText = !["logo", "photo", "principal_signature"].includes(it.k);
                const curAlign = aligns[it.k] || "left";
                return (
                  <div key={it.k} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={`idcard-item-${it.k}`}>
                    <div className="flex flex-col -space-y-1">
                      <button type="button" disabled={idx === 0} onClick={() => move(it.k, -1)} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`idcard-up-${it.k}`}><ChevronUp size={15} /></button>
                      <button type="button" disabled={idx === ordered.length - 1} onClick={() => move(it.k, 1)} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`idcard-down-${it.k}`}><ChevronDown size={15} /></button>
                    </div>
                    <Checkbox checked={enabled} onCheckedChange={(v) => set("idcard_fields", v ? [...new Set([...base, it.k])] : base.filter((x) => x !== it.k))} data-testid={`idcard-toggle-${it.k}`} />
                    <span className="text-sm font-medium text-slate-800 flex-1">{it.label}</span>
                    <div className="flex items-center gap-0.5">
                      {isText ? <>
                        {alignBtn(it.k, "left", AlignLeft, curAlign)}
                        {alignBtn(it.k, "center", AlignCenter, curAlign)}
                        {alignBtn(it.k, "right", AlignRight, curAlign)}
                      </> : <span className="text-xs text-slate-300 w-[66px] text-center">—</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Input type="number" step="0.5" min="1" className="w-20 h-8" value={sizeVal} onChange={(e) => set("idcard_sizes", { ...sizes, [it.k]: parseFloat(e.target.value) || 0 })} disabled={!enabled} data-testid={`idcard-size-${it.k}`} />
                      <span className="text-xs text-slate-400 w-6">{it.unit}</span>
                    </div>
                  </div>
                );
              });
            })()}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
